import rocketAnimation from "@client/animations/rocket.json";
import * as graphics from "@client/app/graphics";
import {IconType, SimpleIcon} from "@client/components/Icon";
import * as auth from "@client/features/auth";
import * as forms from "@client/forms";
import {Lottie} from "@client/hacks";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import {useAppDispatch} from "@client/store";
import {ArrowRightIcon, EnvelopeIcon} from "@heroicons/react/24/outline";
import {classNames, server} from "@reactivated";
import {motion} from "motion/react";
import React from "react";

import {
    OnboardingScreenComponent,
    Shell,
    containerVariants,
    itemVariants,
} from "./PlanOnboarding";

type LoginTab = "link" | "password";

const LOGIN_TABS: {id: LoginTab; label: string; icon: IconType}[] = [
    {id: "link", label: "Email Link", icon: "0154-paper-plane"},
    {id: "password", label: "Use Password", icon: "0108-lock"},
];

function LoginTabSelector({
    selectedTab,
    onSelectTab,
}: {
    selectedTab: LoginTab;
    onSelectTab: (tab: LoginTab) => void;
}) {
    const selectedIndex = LOGIN_TABS.findIndex((tab) => tab.id === selectedTab);

    return (
        <div className="flex justify-center">
            <div className="relative grid grid-cols-2 rounded-3xl border border-stone-200 bg-white/80 p-1.5 backdrop-blur-md">
                <motion.div
                    className="absolute inset-y-1.5 w-[calc(50%-3px)] rounded-xl bg-indigo-100"
                    initial={false}
                    animate={{left: selectedIndex === 0 ? 6 : "calc(50% - 3px)"}}
                    transition={{type: "spring", stiffness: 500, damping: 35}}
                />
                {LOGIN_TABS.map(({id, label, icon}) => {
                    const isSelected = selectedTab === id;

                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => {
                                void hardware.haptic();
                                onSelectTab(id);
                            }}
                            className="relative z-10 flex items-center justify-center gap-1.5 px-4 py-2"
                        >
                            <SimpleIcon
                                icon={icon}
                                className={classNames(
                                    "size-4",
                                    isSelected ? "text-indigo-700" : "text-stone-500",
                                )}
                            />
                            <span
                                className={classNames(
                                    "text-xs font-medium",
                                    isSelected ? "text-indigo-700" : "text-stone-600",
                                )}
                            >
                                {label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function LoginContent(props: {onSuccess: () => void}) {
    const dispatch = useAppDispatch();
    const [loginTab, setLoginTab] = React.useState<LoginTab>(() =>
        window.cordova == null ? "password" : "link",
    );
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const form = forms.useForm(server.core.rpc.Login, {
        value: {email, password},
        onChange: (next) => {
            setEmail(next.email);
            setPassword(next.password);
        },
    });
    const linkEmail = forms.useField(
        {kind: "email", name: "email"},
        {value: email, onChange: setEmail},
    );
    const [state, setState] = React.useState<
        "idle" | "error" | "link_sent" | "link_error"
    >("idle");
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Auto-fill test login credentials if JOY_TEST_LOGIN is set
    React.useEffect(() => {
        const loadTestLogin = async () => {
            const {value} = await hardware.Preferences.get({key: "JOY_TEST_LOGIN"});
            if (value != null) {
                setEmail(value);
                setPassword("test");
            }
        };
        void loadTestLogin();
    }, []);

    const handleLogin = async () => {
        setIsSubmitting(true);
        try {
            const response = await forms.submitForm(form, server.core.rpc.login, {
                toasts: false,
            });
            if (response == null) return;
            if (response.type !== "success") {
                setState("error");
                return;
            }
            await dispatch(auth.postLogin(response.data, true));
            props.onSuccess();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendLink = async () => {
        const validated = linkEmail.validated;
        if (!validated.success) return;
        setIsSubmitting(true);
        const response = await server.core.rpc.request_login_link(validated.data);
        setIsSubmitting(false);

        if (response.type === "success") {
            // If result.data is a Profile (not boolean), user was auto-logged in
            if (typeof response.data !== "boolean") {
                await dispatch(auth.postLogin(response.data, true));
                props.onSuccess();
            } else {
                setState("link_sent");
            }
        } else {
            setState("link_error");
        }
    };

    const handleSubmit = async () => {
        void hardware.haptic();
        if (loginTab === "link") {
            await handleSendLink();
        } else {
            await handleLogin();
        }
    };

    const handleResetPassword = () => {
        void hardware.haptic();
        void router.push("reset.index", {});
    };

    const getNextButton = () => {
        if (loginTab === "link") {
            return {
                onNext: handleSubmit,
                label: state === "link_sent" ? "Sent" : "Send Link",
                icon: EnvelopeIcon,
                iconPosition: "right" as const,
                animateIcon: false,
                disabled: !form.values.email.includes("@") || state === "link_sent",
            };
        }
        return {
            onNext: handleSubmit,
            label: "Sign in",
            icon: ArrowRightIcon,
            iconPosition: "right" as const,
            animateIcon: true,
            disabled: !form.values.email.includes("@") || !form.validated.success,
        };
    };

    return (
        <OnboardingScreenComponent
            animation={null}
            back={null}
            content={
                <motion.div
                    className="flex flex-1 flex-col justify-center gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <motion.h1
                        className="text-center text-3xl font-light text-pretty"
                        variants={itemVariants}
                    >
                        Welcome back
                    </motion.h1>
                    <motion.div variants={itemVariants}>
                        <LoginTabSelector
                            selectedTab={loginTab}
                            onSelectTab={(tab) => {
                                setLoginTab(tab);
                                // Reset state when switching tabs
                                setState("idle");
                            }}
                        />
                    </motion.div>
                    {state === "link_sent" ? (
                        <motion.div
                            className="flex flex-col items-center justify-center gap-4"
                            initial={{opacity: 0, y: 10}}
                            animate={{opacity: 1, y: 0}}
                        >
                            <Lottie
                                animationData={rocketAnimation}
                                loop={true}
                                className="current-color-lottie size-32 rounded-full bg-indigo-100 fill-indigo-100 stroke-indigo-100 p-4 text-indigo-600"
                            />
                            <h2 className="text-center text-3xl font-light">
                                Check your email
                            </h2>
                            <p className="text-center text-base text-stone-600">
                                Link sent to{" "}
                                <span className="font-medium">{email}</span>
                            </p>
                            <button
                                type="button"
                                className="mt-4 text-base font-medium text-indigo-600"
                                onClick={() => setState("idle")}
                            >
                                Use a different email
                            </button>
                        </motion.div>
                    ) : (
                        <form
                            className="flex flex-col gap-6"
                            onSubmit={(e) => {
                                e.preventDefault();
                                void handleSubmit();
                            }}
                        >
                            <motion.label
                                className="flex flex-col gap-2 text-lg font-light"
                                variants={itemVariants}
                            >
                                <div>Email</div>
                                <div className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500">
                                    <input
                                        type="email"
                                        name="email"
                                        autoComplete="username"
                                        enterKeyHint={
                                            loginTab === "password" ? "next" : "send"
                                        }
                                        placeholder="Enter your email"
                                        className="flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-light placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                                        value={email}
                                        onChange={(e) => {
                                            form.fields.email.onChange(e.target.value);
                                            if (state === "link_error") {
                                                setState("idle");
                                            }
                                        }}
                                    />
                                </div>
                            </motion.label>

                            {state === "link_error" && (
                                <motion.p
                                    className="text-sm text-red-500"
                                    initial={{opacity: 0, y: -5}}
                                    animate={{opacity: 1, y: 0}}
                                >
                                    Something went wrong. Please try again.
                                </motion.p>
                            )}

                            <label
                                className={classNames(
                                    "flex flex-col gap-2 text-lg font-light",
                                    loginTab !== "password" && "pointer-events-none",
                                )}
                                aria-hidden={loginTab !== "password"}
                            >
                                <motion.div
                                    initial={false}
                                    animate={{
                                        opacity: loginTab === "password" ? 1 : 0,
                                        y: loginTab === "password" ? 0 : 8,
                                    }}
                                    transition={{duration: 0.2, delay: 0}}
                                >
                                    Password
                                </motion.div>
                                <motion.div
                                    className="flex w-full items-center rounded-sm border border-stone-300 bg-white pr-1 has-focus:ring-2 has-focus:ring-indigo-500"
                                    initial={false}
                                    animate={{
                                        opacity: loginTab === "password" ? 1 : 0,
                                        y: loginTab === "password" ? 0 : 8,
                                    }}
                                    transition={{duration: 0.2, delay: 0.05}}
                                >
                                    <input
                                        type="password"
                                        name="password"
                                        autoComplete="current-password"
                                        enterKeyHint="go"
                                        placeholder="Enter your password"
                                        disabled={loginTab !== "password"}
                                        tabIndex={loginTab === "password" ? 0 : -1}
                                        className="flex-1 border-0 bg-transparent px-6 py-4 placeholder:font-light placeholder:text-stone-400 focus:ring-0 focus:outline-hidden"
                                        value={password}
                                        onChange={(e) =>
                                            form.fields.password.onChange(
                                                e.target.value,
                                            )
                                        }
                                    />
                                </motion.div>
                                {state === "error" && (
                                    <motion.p
                                        className="text-sm text-red-500"
                                        initial={{opacity: 0, y: -5}}
                                        animate={{opacity: 1, y: 0}}
                                    >
                                        Invalid credentials. Please try again.
                                    </motion.p>
                                )}
                                <motion.button
                                    type="button"
                                    tabIndex={loginTab === "password" ? 0 : -1}
                                    className="text-left text-sm font-medium text-indigo-600"
                                    onClick={handleResetPassword}
                                    initial={false}
                                    animate={{
                                        opacity: loginTab === "password" ? 1 : 0,
                                        y: loginTab === "password" ? 0 : 8,
                                    }}
                                    transition={{duration: 0.2, delay: 0.1}}
                                >
                                    Forgot your password?
                                </motion.button>
                            </label>
                            <button type="submit" className="hidden" />
                        </form>
                    )}
                </motion.div>
            }
            next={getNextButton()}
            isSubmitting={isSubmitting}
            footer={
                <>
                    Don't have an account?{" "}
                    <button
                        type="button"
                        className="font-medium text-indigo-600"
                        onClick={() => {
                            void hardware.haptic();
                            void router.push("register.index", {});
                        }}
                    >
                        Sign up
                    </button>
                </>
            }
            route="start"
            progress={{current: null, total: 0}}
        />
    );
}

function useLogin(): {
    view: React.ReactNode;
    actions: [];
    options: {
        titleSection: React.ReactNode;
        title: string;
        scene: {kind: "EMPTY"};
    };
} {
    const handleSuccess = () => {
        const url = new URL(window.location.href);
        const next = url.searchParams.get("next");

        if (next != null) {
            window.location.href = next;
        } else {
            void router.push("today.journal", {});
        }
    };

    return {
        view: <LoginContent onSuccess={handleSuccess} />,
        actions: [],
        options: {
            titleSection: <graphics.Logo className="-mb-2 h-9 w-auto py-1" />,
            title: "Sign in",
            scene: {kind: "EMPTY"},
        },
    };
}

function MagicLinkLoginHandler() {
    const dispatch = useAppDispatch();
    const [state, setState] = React.useState<"loading" | "error">("loading");

    React.useEffect(() => {
        const validateToken = async () => {
            const url = new URL(window.location.href);
            const token = url.searchParams.get("sesame");

            if (token == null) {
                setState("error");
                return;
            }

            const response = await server.core.rpc.validate_login_link({token});

            if (response.type === "success") {
                await dispatch(auth.postLogin(response.data, true));
                void router.push("today.journal", {});
            } else {
                setState("error");
            }
        };

        void validateToken();
    }, [dispatch]);

    if (state === "error") {
        return (
            <Shell>
                <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
                    <h2 className="text-center text-xl font-light">
                        Invalid or expired link
                    </h2>
                    <p className="text-center text-sm text-stone-500">
                        This sign-in link is no longer valid. Please request a new one.
                    </p>
                    <button
                        type="button"
                        className="mt-4 text-sm font-medium text-indigo-600"
                        onClick={() => router.push("login.index", {})}
                    >
                        Back to sign in
                    </button>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <div className="flex flex-1 items-center justify-center">
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    className="text-stone-500"
                >
                    Signing in...
                </motion.div>
            </div>
        </Shell>
    );
}

function useMagicLinkLogin(): {
    view: React.ReactNode;
    actions: [];
    options: {
        titleSection: React.ReactNode;
        title: string;
        scene: {kind: "EMPTY"};
    };
} {
    return {
        view: <MagicLinkLoginHandler />,
        actions: [],
        options: {
            titleSection: <graphics.Logo className="-mb-2 h-9 w-auto py-1" />,
            title: "Sign in",
            scene: {kind: "EMPTY"},
        },
    };
}

function LoginView() {
    const {view} = useLogin();
    return <>{view}</>;
}

export const Login = router.Login.withHooks(() => ({}))
    .withIndex(LoginView)
    .withActions(() => [])
    .withOptions(() => ({
        title: "Sign in",
        scene: {kind: "EMPTY"},
    }));

function MagicLinkLoginView() {
    const {view} = useMagicLinkLogin();
    return <>{view}</>;
}

export const MagicLinkLogin = router.MagicLinkLogin.withHooks(() => ({}))
    .withIndex(MagicLinkLoginView)
    .withActions(() => [])
    .withOptions(() => ({
        title: "Sign in",
        scene: {kind: "EMPTY"},
    }));
